import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from './_virtual/_rollupPluginBabelHelpers.js';
import React, { createContext, useContext, useRef, useCallback, useState, useMemo } from 'react';
import { get, errorsEqual, set } from './util.js';

var FormContext = createContext();
/**
 * Fetches data, metadata, and callback, for a form field.
 *
 * NOTE: The first value encountered when rendering will be considered its
 *       original value, the component instance has to be re-rendered with
 *       a new key to consider it being a new non-dirty instance.
 */

var useFormField = function useFormField(name, def) {
  var _get;

  if (def === void 0) {
    def = "";
  }

  var formData = useContext(FormContext);

  if (!formData) {
    throw new Error("useFormField() can only be used inside a <Form />.");
  }

  var data = formData.data,
      formErrors = formData.errors,
      idPrefix = formData.idPrefix,
      update = formData.update,
      submitted = formData.submitted;
  var value = String((_get = get(data, name, def)) !== null && _get !== void 0 ? _get : ""); // Save the first render value in a ref so we can keep comparing it

  var original = useRef(value);
  var dirty = value !== original.current;
  var errors = formErrors.filter(function (_ref) {
    var field = _ref.field;
    return field === name;
  });
  var id = idPrefix ? idPrefix + "." + name : name;
  var onChange = useCallback(function (_ref2) {
    var target = _ref2.target;
    return update(name, target.type === "checkbox" ? target.checked : target.value);
  }, [name, update]);
  return {
    dirty: dirty,
    errors: errors,
    submitted: submitted,
    inputProps: {
      id: id,
      name: name,
      onChange: onChange,
      value: value
    }
  };
};
var Form = function Form(props) {
  var children = props.children,
      errors = props.errors,
      name = props.name,
      onChange = props.onChange,
      onError = props.onError,
      onSubmit = props.onSubmit,
      value = props.value,
      formProps = _objectWithoutPropertiesLoose(props, ["children", "errors", "name", "onChange", "onError", "onSubmit", "value"]); // If we have tried to submit the form


  var _useState = useState(false),
      submitted = _useState[0],
      setSubmitted = _useState[1];

  var prevErrors = useRef(errors); // Update errors if they are not equal

  if (!errorsEqual(prevErrors.current, errors)) {
    prevErrors.current = errors;
  }

  var stateErrors = prevErrors.current; // Only change the form context when the data/errors change

  var state = useMemo(function () {
    return {
      data: value,
      errors: stateErrors || [],
      idPrefix: name,
      update: function update(name, newItemValue) {
        var newValue = set(value, name, newItemValue);

        if (newValue !== value) {
          onChange(newValue);
        }
      },
      submitted: submitted
    };
  }, [name, stateErrors, onChange, value, submitted]);
  var handleSubmit = useCallback(function (e) {
    setSubmitted(true);

    if (errors && errors.length > 0) {
      e.preventDefault();
      e.stopPropagation();

      if (onError) {
        onError(e, errors, value);
      }

      return false;
    }

    onSubmit(e, value);
  }, [errors, onError, onSubmit, value]);
  return React.createElement("form", _extends({}, formProps, {
    onSubmit: handleSubmit
  }), React.createElement(FormContext.Provider, {
    value: state
  }, children));
};

export { Form, FormContext, useFormField };
//# sourceMappingURL=form.js.map
